import { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '@/lib/api-client';

export type DeployNoticeSeverity = 'info' | 'warning' | 'urgent';

export interface DeployNotice {
  enabled: boolean;
  message: string;
  scheduledAt?: string;
  severity: DeployNoticeSeverity;
  expiresAt?: string;
}

export interface SystemStatus {
  maintenanceMode: boolean;
  deployMode: 'normal' | 'drain';
  deployNotice: DeployNotice | null;
  platformName: string;
}

const POLL_MS = 60_000;
const SYSTEM_STATUS_EVENT = 'larkpm:system-status';

async function fetchSystemStatus(): Promise<SystemStatus> {
  const res = await fetch(`${API_BASE_URL}/system-status`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`System status unavailable (${res.status})`);
  }
  return res.json();
}

export function useSystemStatus() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchSystemStatus();
      setStatus(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load system status'));
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = window.setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    const onWs = (ev: Event) => {
      const detail = (ev as CustomEvent<SystemStatus>).detail;
      if (detail) setStatus(detail);
      else refresh();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener(SYSTEM_STATUS_EVENT, onWs);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(SYSTEM_STATUS_EVENT, onWs);
    };
  }, [refresh]);

  return { status, error, refresh };
}

export function dispatchSystemStatusUpdate(payload: SystemStatus) {
  window.dispatchEvent(new CustomEvent(SYSTEM_STATUS_EVENT, { detail: payload }));
}
